import React, { useContext, SyntheticEvent, FunctionComponent, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// MUI
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';

// MUI Icons
import DateRangeIcon from '@mui/icons-material/DateRange';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import { ContentCopy, Delete, Tour } from '@mui/icons-material';

// Custom Components
import useFormController from '@/components/useFormController';
import DynamicDialog from '@/ui/Dialog/DynamicDialog';
import DialogHeader from '@/ui/Dialog/DialogHeader';
import DialogFooter from '@/ui/Dialog/DialogFooter';
import DialogToolbarButtonClose from '@/ui/Dialog/ToolbarButtons/DialogToolbarButtonClose';
import ToolbarFillContent from '@/ui/Toolbar/ToolbarFillContent';
import DialogBody from '@/ui/Dialog/DialogBody';
import DialogActionButtonClose from '@/ui/Dialog/ActionButtons/DialogActionButtonClose';
import DialogActionButtonAdd from '@/ui/Dialog/ActionButtons/DialogActionButtonAdd';
import DialogActionButtonSave from '@/ui/Dialog/ActionButtons/DialogActionButtonSave';
import DialogActionButtonCancel from '@/ui/Dialog/ActionButtons/DialogActionButtonCancel';
import DialogToolbarMenuButton from '@/ui/Dialog/DialogToolbarMenuButton';
import DialogToolbarMenuItem from '@/ui/Dialog/DialogToolbarMenuItem';
import DialogContext from '@/ui/DialogContext/DialogContext';
import SnackbarContext from '@/ui/SnackbarContext/SnackbarContext';
import UserContext from '@/components/UserContext/UserContext';
import { GridContainer } from '@/ui/Grid';
import FormContent from '@/components/FormContent';
import { Permissions } from '@/lib/permissions';
import FormAvatar from '@/ui/Form/FormAvatar';
import FormHeading from '@/ui/Form/FormHeading';
import { FormTab, FormTabPanel, FormTabs } from '@/ui/Form';
import FormHeadingIzletData from '@/ui/Form/FormHeadingIzletData';

import IzletDaniPane from '@/views/Izleti/Forms/Panes/IzletDaniPane';

// Types
import { ClosingDetails, FormControllerProps } from '@/@types/components/formController';
import { IDialogProps } from '@/@types/ui/DialogContext';


const REGIzletForm: FunctionComponent<IDialogProps> = (props) => {
  // HOOKS
  const dialogContext = useContext(DialogContext);
  const snackbarContext = useContext(SnackbarContext);
  const userContext = useContext(UserContext);
  const { t } = useTranslation();

  const {
    record,
    validation,
    dataChanged,
    fields,
    subModels,
    onFieldChange,
    doInsert,
    doUpdate,
    doClose,
    doRefresh,
    doDelete,
  } = useFormController(props as FormControllerProps);

  // STATE
  const [changeMade, setChangeMade] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);

  // PROPS
  const { dc, mode, form, recordId, onClose } = props;

  // VARIABLES
  const hasKomisijaPerm = userContext?.hasAnyPermission([Permissions.HPSKomisijaView]);
  const hasUserEditPerm = userContext?.hasAnyPermission([Permissions.UserEdit]);

  const disableSave = dataChanged ? false : true;
  const disablePanes = mode === 'insert' || !record.id;

  const allowCopy = mode === 'view' && hasUserEditPerm;
  const allowEdit = mode === 'update' && hasUserEditPerm || hasKomisijaPerm;
  // const allowActivate = mode === 'update' && hasKomisijaPerm;

  const hasToolbarActions = allowEdit || allowCopy;

  const formTitle = mode === 'insert' ? (t('titles.izlet_new') as string) : `${t('titles.izlet') as string} - ${record.odrediste}`;
  const icon = <Tour />;
  const status = record['status_id'] as number;

  let cmode = mode;
  if (record.active === false || status === 30) {
    cmode = 'view';
  }

  // USEEFFECTS
  useEffect(() => {
    const handlePopState = (event: any) => {
      // Prevent going back by pushing the state again
      window.history.pushState(null, '', window.location.href);
      dialogContext.hideDialog(); // Call your custom dialog hide function
    };

    // Push state to prevent back navigation
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // CORE ACTIONS
  const handleFieldChange = (value: any, source: string) => {
    if (onFieldChange) {
      onFieldChange(value, source);
    }
  };

  const close = (result: ClosingDetails, dontHide: boolean = false) => {
    if (result.dataChanged || dataChanged) {
      onClose({ dataChanged: true, action: result.action });
    }

    if (!dontHide) {
      dialogContext.hideDialog();
    }
  };

  const handleTabChange = (value: number) => {
    if (!disableSave && currentTab === 0 && mode === "update") {
      handleUpdate();
    }
    if (value === 1) {
      if (record.prvi_dan) {
        setCurrentTab(value);
      } else {
        setCurrentTab(0);
        snackbarContext.showNotification('izleti.warnings.prvi_dan', 'warning');
      }
    } else {
      setCurrentTab(value);
    }
  };

  // HANDLE ACTIONS
  const handleInsert = (evt: SyntheticEvent) => {
    if (doInsert) {
      doInsert()
        .then((result) => {
          if (result.success) {
            snackbarContext.showNotification(t('notifications.insert'), 'success');
            dc.changeApiPath((dc.apiPath as string).replace('?action=copy', '')); // recover from copying if necessary
            dialogContext.updateDialogProps(1, { mode: 'update', recordId: result.id, initialRecord: undefined });
            close({ dataChanged: true, action: 'insert' }, true);
            setCurrentTab(1);
          } else if (result.error) {
            const msg = t('error.addRecord') + result.error;
            snackbarContext.showNotification(msg, 'error');
          } else if (result.validationPass === false) {
            // do nothing
          }
        })
        .catch((resp) => {
          snackbarContext.showNotification(resp.error, 'error');
        });
    }
  };

  const handleUpdate = (evt?: SyntheticEvent) => {
    if (doUpdate) {
      doUpdate().then((result) => {
        if (result.success) {
          setChangeMade(true);
          snackbarContext.showNotification(t('notifications.update'), 'success');
          doRefresh();
          // close({ dataChanged: true, action: 'update' });
        } else if (result.error) {
          const msg = t('error.editRecord') + result.error;
          snackbarContext.showNotification(msg, 'error');
        } else if (result.validationPass === false) {
          // do nothing
        }
      });
    }
  };

  const handleClose = (evt: SyntheticEvent) => {
    if (doClose) {
      doClose()
        .then((result) => {
          if (result.success) {
            close({ dataChanged: true, action: 'cancel' });
          } else {
            if (result.shouldSave) {
              handleUpdate(evt);
            } else if (result.canceled) {
              //do nothing
            }
          }
        })
        .catch((result) => {
          console.log(result);
        });
    }
  };

  const handleDelete = () => {
    doDelete().then((result) => {
      if (result.success) {
        snackbarContext.showNotification(t('notifications.delete'), 'success');
        close({ dataChanged: true, action: 'delete' });
      } else if (result.error) {
        const msg = t('error.delete') + result.error;
        snackbarContext.showNotification(msg, 'error');
      }
    });
  };

  const handleCopy = () => {
    const { odrediste, trajanje_dani, interna_napomena } = record;
    snackbarContext.showNotification('notifications.copying_izlet', 'success');
    dc.changeApiPath(`${dc.apiPath}?action=copy`);
    dialogContext.updateDialogProps(1, {
      recordId: undefined,
      mode: 'insert',
      initialRecord: {
        id: recordId,
        odrediste,
        trajanje_dani,
        interna_napomena,
      },
    });
  };

  const handleNextStepWithUpdate = (evt: SyntheticEvent) => {
    if (!disableSave && currentTab === 0 && mode === "update") {
      handleUpdate(evt);
    } 
    setCurrentTab(prev => prev + 1);
  }

  // RENDER
  return (
    <DynamicDialog open forceFullscreen onClose={handleClose}>
      <DialogHeader>
        <Toolbar variant="dense" disableGutters>
          <FormAvatar icon={icon} ariaLabel="poi" />
          <FormHeading
            header={formTitle}
            subheaderContent={mode !== 'insert' && record.id ? <FormHeadingIzletData record={record} disableStatus/> : undefined}
          />
          <ToolbarFillContent />
          {hasToolbarActions ? (
            <DialogToolbarMenuButton>
              {allowCopy ? (
                <DialogToolbarMenuItem
                  onClick={() => {
                    handleCopy();
                  }}
                  label={t('buttons.copy_izlet')}
                  icon={<ContentCopy />}
                />
              ) : null}
              {mode === 'update' ? <DialogToolbarMenuItem onClick={handleDelete} label={t('buttons.delete')} icon={<Delete />} /> : null}
            </DialogToolbarMenuButton>
          ) : null}

          <DialogToolbarButtonClose onClick={handleClose} />
        </Toolbar>
      </DialogHeader>
      <DialogBody>
        <FormTabs value={currentTab} onChange={handleTabChange}>
          <FormTab id="basic-tab" label={t('common.tabs.basic')} icon={<InfoOutlineIcon />} aria-label="basic" value={0} children={undefined} />
          <FormTab
            id="dani-tab"
            label={t('titles.plan_izleta')}
            icon={<DateRangeIcon />}
            aria-label="dani"
            value={1}
            disabled={disablePanes}
            children={undefined}
          />
        </FormTabs>
        <FormTabPanel value={currentTab} index={0} mountInvisible>
          <Box m={0} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box m={2} maxWidth="lg" alignContent="center">
              <GridContainer>
                <FormContent
                  fieldNames={dc.getFormFieldsNames(form)}
                  record={record}
                  validation={validation}
                  fields={fields}
                  onFieldChange={handleFieldChange}
                  mode={cmode}
                  subModels={subModels}
                  columns={2}
                />
              </GridContainer>
            </Box>
          </Box>
        </FormTabPanel>
        {record.id ? (
            <FormTabPanel value={currentTab} index={1} mountInvisible>
              <IzletDaniPane variant="reg" mode={cmode} izletId={record.id as number} excursionIsCroatian/>
            </FormTabPanel>
        ) : null}
      </DialogBody>
      <DialogFooter>
        {cmode === 'view' || cmode === 'update' ? (
          <DialogActionButtonClose variant="outlined" onClick={handleClose} />
        ) : (
          <DialogActionButtonCancel variant="outlined" onClick={handleClose} />
        )}
        {currentTab !== 1 ? (
          <>
            {cmode === 'insert' ? <DialogActionButtonAdd next variant="contained" onClick={handleInsert} /> : null}
            {cmode === 'update' ? <DialogActionButtonAdd next variant="contained" onClick={handleNextStepWithUpdate} /> : null}
          </>
        ) : null}
      </DialogFooter>
    </DynamicDialog>
  );
};

export default REGIzletForm;
